import * as st from './st';
import { TYPES } from './buildUtils';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { debugLog } from '../utils/debugLog';

const TXT_OR = { fr: 'ou', en: 'or', de: 'oder', es: 'o', pt: 'ou' };
const TXT_AND = { fr: 'et', en: 'and', de: 'und', es: 'y', pt: 'e' };

// Trophy set-bonus condition, Dofus 3.7 (`pk<2`): the wearer must have strictly
// fewer equipped sets than this. Mirrors RESTRICTED_TROPHY_MAX_SETS in
// core/enums/criteria.py. The 3.7 client says "Nombre de panoplies équipées";
// the noun phrase alone reads better in a one-line tooltip and the other four
// locales are ours anyway (the client still has them untranslated, "[!] ...").
const RESTRICTED_TROPHY_MAX_SETS = 2;
const TXT_EQUIPPED_SETS = {
    fr: 'Panoplies équipées',
    en: 'Equipped sets',
    de: 'Ausgerüstete Sets',
    es: 'Sets equipados',
    pt: 'Conjuntos equipados',
};

/**
 * Check if a stat ID exists in the STATS list
 * @param {string|number} statId - The stat ID to check
 * @returns {boolean} True if the stat ID is in STATS list
 */
const isStatInList = (statId) => {
    return st.STATS.some(s => String(s.id) === String(statId));
};

/**
 * Takes raw item data and enriches it with the full type object.
 * @param {Array|Object} itemsData - A single item or an array of items.
 * @returns {Array|Object} The enriched item(s) with a .type property.
 */
export const processItemData = (itemsData) => {
    const typeById = new Map(TYPES.map(t => [t.id, t]));

    const enrichItem = (item) => {
        if (!item) return null;
        const type = item.type || typeById.get(item.item_type_id) || null;
        return {
            ...item,
            icon: resolveMediaUrl(item.icon),
            type,
        };
    };

    if (Array.isArray(itemsData)) {
        return itemsData.map(enrichItem);
    }
    return enrichItem(itemsData);
};

/**
 * Process item effects map into a standardized stats array for StatsList component
 *
 * Rows carry no label: StatsList resolves the name from `st.STATS` against its
 * own `lang`. This used to take a `lang` of its own and ignore it, which read
 * like the language was handled here and hid the fact that four call sites were
 * not passing `lang` to StatsList at all — item cards printed French stat names
 * under English item names.
 *
 * @param {Object} effectsMap - The effects_map from an item
 * @returns {Array} Array of stat objects with icon, value, and id properties
 */
export const processItemStats = (effectsMap) => {
    if (!effectsMap) return [];

    const statRows = [];
    for (const [statId, value] of Object.entries(effectsMap)) {
        if (!isStatInList(statId)) {
            continue;
        }
        const stat = st.STATS.find(s => String(s.id) === String(statId));
        if (stat) {
            const icon = stat.icon;
            statRows.push({ id: stat.id, icon, value });
        }
    }
    return statRows;
};

/**
 * Process item conditions into a readable format
 *
 * Vitality thresholds are stored on the *characteristic* (`Vitalité > 3949` on Gargandyas
 * gear) while our Vitality row — and every number on the sheet next to this tooltip — is
 * the HP total. Printing the raw value reads as "already cleared" at 4002 HP, so the base
 * PV of the character's level is added back on for display: `> 4999` at level 200, which
 * is the 5000 HP the game actually asks for. Without a level the raw value is kept.
 *
 * @param {Array} criteria - The criteria array from an item
 * @param {string} lang - Language code ('fr' or 'en')
 * @param {number|null} lvl - Character level the conditions are read against
 * @returns {string} Formatted conditions string
 */
export const processItemConditions = (criteria, lang = 'fr', lvl = null) => {

    if (!criteria || !Array.isArray(criteria) || criteria.length === 0) {
        return null;
    }

    // Get the first criteria object (there's usually only one)
    const criteriaObj = criteria[0];
    if (!criteriaObj || !Array.isArray(criteriaObj.conditions) || criteriaObj.conditions.length === 0) {
        debugLog('No valid conditions found in criteria object');
        return null;
    }

    const conditions = criteriaObj.conditions;
    const operators = criteriaObj.operators || [];


    return conditions.map((condition, index) => {
        const stat = st.STATS.find(s => String(s.id) === String(condition.characteristic));
        const statName = stat ? stat.label[lang] : `Stat ${condition.characteristic}`;
        const operator = condition.operator || '';
        const isVitality = String(condition.characteristic) === String(st.VITALITY_ID);
        const value = isVitality && lvl
            ? condition.value + st.getBaseVitality(lvl)
            : condition.value;

        let formattedCondition = `${statName} ${operator} ${value}`;

        // Add operator between conditions if available
        if (index < conditions.length - 1 && operators[index]) {
            const op = operators[index];
            if (op === '|') {
                formattedCondition += ` ${TXT_OR[lang] || TXT_OR.en} `;
            } else if (op === '&') {
                formattedCondition += ` ${TXT_AND[lang] || TXT_AND.en} `;
            } else {
                formattedCondition += ` ${op} `;
            }
        }

        return formattedCondition;
    }).join('');
};

/**
 * Process all item prerequisite sources into a readable format.
 * Some trophy restrictions are encoded as a boolean (`restricted`) instead of criteria rows.
 * @param {Object} item
 * @param {string} lang
 * @param {number|null} lvl - Character level, so Vitality thresholds print in sheet units
 * @returns {string|null}
 */
export const processItemRequirementText = (item, lang = 'fr', lvl = null) => {
    if (!item) return null;

    if (item.criteria && Array.isArray(item.criteria) && item.criteria.length > 0) {
        return processItemConditions(item.criteria, lang, lvl);
    }

    if (item.restricted === true) {
        const label = TXT_EQUIPPED_SETS[lang] || TXT_EQUIPPED_SETS.en;
        return `${label} < ${RESTRICTED_TROPHY_MAX_SETS}`;
    }

    return item.criteria_processed || null;
};

/**
 * Process set bonuses into a standardized stats array for StatsList component
 * @param {Array} bonuses - Array of bonus objects from set data
 * @param {string} lang - Language code ('fr' or 'en')
 * @returns {Array} Array of stat objects with icon, value, and id properties
 */
export const processSetBonuses = (bonuses, lang = 'fr', caps = null) => {
    if (!bonuses || !Array.isArray(bonuses)) {
        return [];
    }

    const processedBonuses = [];
    const bonusMap = new Map(); // Use Map to aggregate bonuses by stat ID

    bonuses.forEach((bonusObj, index) => {
        // Handle flat array of bonus objects (new format)
        if (bonusObj && bonusObj.id && bonusObj.value !== undefined) {
            const statId = bonusObj.id;
            const currentValue = bonusMap.get(statId) || 0;
            bonusMap.set(statId, currentValue + bonusObj.value);
        }
        // Handle nested array format (old format) for backward compatibility
        else if (Array.isArray(bonusObj)) {
            bonusObj.forEach((bonusEffect, effectIndex) => {
                if (bonusEffect && bonusEffect.characteristic && bonusEffect.value) {
                    const statId = bonusEffect.characteristic;
                    const currentValue = bonusMap.get(statId) || 0;
                    bonusMap.set(statId, currentValue + bonusEffect.value);
                }
            });
        }
    });

    // Convert Map back to array format, only including stats in STATS list
    bonusMap.forEach((totalValue, statId) => {
        if (!isStatInList(statId)) {
            return;
        }
        const stat = st.STATS.find(s => s.id === statId);
        if (stat) {
            processedBonuses.push({
                id: stat.id,
                icon: stat.icon,
                value: totalValue
            });
        }
    });

    // Ceilings the tier puts on a characteristic, kept out of the sum above: a
    // ceiling added to a bonus row would read as one. They carry `isCap` so the
    // renderer says "max 2" instead of "+2". See backend core/set_caps.py.
    (caps || []).forEach(cap => {
        const statId = cap?.characteristic;
        if (!isStatInList(statId)) return;
        const stat = st.STATS.find(s => s.id === statId);
        if (!stat) return;
        processedBonuses.push({
            id: stat.id,
            icon: stat.icon,
            value: Number(cap.value),
            isCap: true,
        });
    });

    return processedBonuses;
}; 
